// ============================================
// ADDRESS AUTOCOMPLETE COMPONENT
// Uses Google Places API for address suggestions
// Place in: /src/components/AddressAutocomplete.js
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader, Search } from 'lucide-react';

const AddressAutocomplete = ({ 
  onAddressSelect, 
  initialValue = '',
  placeholder = 'Start typing your address...',
  googleMapsApiKey 
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const inputRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const wrapperRef = useRef(null);

  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      setScriptLoaded(true);
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setScriptLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      return () => clearInterval(checkLoaded);
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => console.error('Failed to load Google Maps script');
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, [googleMapsApiKey]);

  // Initialize services when script loads
  useEffect(() => {
    if (scriptLoaded && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      // Create a dummy div for PlacesService (required)
      const dummyElement = document.createElement('div');
      placesService.current = new window.google.maps.places.PlacesService(dummyElement);
    }
  }, [scriptLoaded]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggestions
  const fetchSuggestions = (input) => {
    if (!input || input.length < 3 || !autocompleteService.current) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    
    autocompleteService.current.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: 'us' }, // US addresses only
        types: ['address']
      },
      (predictions, status) => {
        setLoading(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
        }
      }
    );
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Debounce API calls
    clearTimeout(window.addressDebounce);
    window.addressDebounce = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelect = (placeId, description) => {
    setInputValue(description);
    setShowSuggestions(false);
    setSuggestions([]);

    if (!placesService.current) return;

    // Get full place details
    placesService.current.getDetails(
      {
        placeId,
        fields: ['address_components', 'formatted_address']
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const addressComponents = place.address_components;
          
          // Parse address components
          const getComponent = (types) => {
            const component = addressComponents.find(c => 
              types.some(type => c.types.includes(type))
            );
            return component ? component.short_name : '';
          };

          const getLongComponent = (types) => {
            const component = addressComponents.find(c => 
              types.some(type => c.types.includes(type))
            );
            return component ? component.long_name : '';
          };

          const streetNumber = getComponent(['street_number']);
          const streetName = getLongComponent(['route']);
          const city = getLongComponent(['locality', 'sublocality', 'sublocality_level_1', 'neighborhood']);
          const state = getComponent(['administrative_area_level_1']);
          const postalCode = getComponent(['postal_code']);
          const unit = getComponent(['subpremise']);

          const streetAddress = streetNumber && streetName 
            ? `${streetNumber} ${streetName}` 
            : streetName || description.split(',')[0];

          // Call parent callback with parsed address
          onAddressSelect({
            streetAddress,
            unit,
            city,
            state,
            postalCode,
            formattedAddress: place.formatted_address
          });
        }
      }
    );
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', marginBottom: '1rem' }}>
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        color: '#94a3b8',
        fontSize: '0.85rem',
        marginBottom: '0.5rem',
        fontWeight: '500'
      }}>
        <Search size={16} />
        Search Address
        <span style={{ color: '#ef4444' }}>*</span>
      </label>
      
      <div style={{ position: 'relative' }}>
        <MapPin 
          size={18} 
          color="#64748b" 
          style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none'
          }}
        />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: '0.75rem 1rem 0.75rem 2.5rem',
            background: '#1e293b',
            border: '1px solid #475569',
            borderRadius: '0.5rem',
            color: '#e2e8f0',
            fontSize: '16px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
        {loading && (
          <Loader 
            size={18} 
            color="#6366f1"
            style={{
              position: 'absolute',
              right: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              animation: 'spin 1s linear infinite'
            }}
          />
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background: '#1e293b',
          border: '1px solid #475569',
          borderRadius: '0.5rem',
          marginTop: '0.25rem',
          maxHeight: '250px',
          overflowY: 'auto',
          zIndex: 1000,
          boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
        }}>
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.place_id}
              onClick={() => handleSelect(suggestion.place_id, suggestion.description)}
              style={{
                padding: '0.75rem 1rem',
                cursor: 'pointer',
                borderBottom: '1px solid #334155',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                transition: 'background 0.15s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#334155'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <MapPin size={16} color="#6366f1" style={{ marginTop: '2px', flexShrink: 0 }} />
              <div>
                <div style={{ color: '#e2e8f0', fontSize: '0.9rem' }}>
                  {suggestion.structured_formatting.main_text}
                </div>
                <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.125rem' }}>
                  {suggestion.structured_formatting.secondary_text}
                </div>
              </div>
            </div>
          ))}
          <div style={{
            padding: '0.5rem 1rem',
            background: '#0f172a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.25rem'
          }}>
            <span style={{ color: '#64748b', fontSize: '0.7rem' }}>Powered by</span>
            <img 
              src="https://developers.google.com/static/maps/documentation/images/google_on_white.png"
              alt="Google"
              style={{ height: '14px', opacity: 0.7 }}
            />
          </div>
        </div>
      )}

      {/* No script loaded fallback message */}
      {!scriptLoaded && (
        <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.25rem' }}>
          Loading address search...
        </p>
      )}

      <style>{`
        @keyframes spin {
          from { transform: translateY(-50%) rotate(0deg); }
          to { transform: translateY(-50%) rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AddressAutocomplete;
